import type { DragEvent } from "react";

/**
 * The drag payload a candidate image carries into a drop target.
 *
 * A custom MIME type on purpose. An `<img>` dragged in a browser also produces
 * the browser's own `text/uri-list`/`text/html` payload — and for an image the
 * panel renders from a `blob:` object URL, that payload names the blob, which
 * no server can download. Writing our own type keeps the URL the drop target
 * acts on the one the *source* chose, whatever the browser would have sent.
 * `text/uri-list` is written alongside it so a drop outside the app (another
 * tab, the desktop) still carries something useful.
 */
export const IMAGE_DRAG_MIME = "application/x-tina-image-url";

/** Data URL scheme, written as a constant so the guards below read plainly. */
const DATA_URL_PREFIX = "data:";

/** Publish `url` as the payload of a drag started on a candidate image. */
export function setImageDragData(event: DragEvent, url: string): void {
  event.dataTransfer.setData(IMAGE_DRAG_MIME, url);
  event.dataTransfer.setData("text/uri-list", url);
  event.dataTransfer.effectAllowed = "copy";
}

/**
 * The http(s) image URL a drop carries, or `null` when it carries none.
 *
 * Only absolute `http(s)` URLs are accepted. A `blob:` URL is scoped to the
 * page that made it and a `data:` URL is already a file the drop's own file
 * channel handles, so neither is something a drop target can act on here.
 */
export function readDroppedImageUrl(
  dataTransfer: DataTransfer | null,
): string | null {
  if (!dataTransfer) return null;

  const payload =
    dataTransfer.getData(IMAGE_DRAG_MIME) ||
    dataTransfer.getData("text/uri-list") ||
    dataTransfer.getData("text/plain");

  for (const line of payload.split("\n")) {
    const candidate = line.trim();
    // `text/uri-list` is a comment-capable format: `#` starts a comment line.
    if (!candidate || candidate.startsWith("#")) continue;
    if (candidate.startsWith("http://") || candidate.startsWith("https://")) {
      return candidate;
    }
  }
  return null;
}

/**
 * Whether a value is a URL the drop path produced rather than bytes read from
 * a local file or the clipboard.
 *
 * A caller that must upload bytes (e.g. to Firebase Storage) has nothing to
 * upload for a dragged candidate image, and fetching it in the browser would
 * be a cross-origin request against a host that need not allow one — so it
 * passes a URL like this straight through to whatever downloads it server
 * side. `data:` URLs are excluded: those are the bytes.
 */
export function isDroppedImageUrl(value: string | null): boolean {
  if (!value) return false;
  return (
    !value.startsWith(DATA_URL_PREFIX) &&
    (value.startsWith("http://") || value.startsWith("https://"))
  );
}
